import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';

// Privacy Notice for Go Doorly, LLC. Static informational page — public
// route, no auth. Linked from OfferAcceptedReview footer + Step 4 GLBA
// privacy disclosure surface.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🔑 "LAST UPDATED" COMES FROM THE SERVER — the same constant the consent records as the privacy
// version (backend/constants/accountConsents.js DOCUMENTS.privacy), so the page and the record cannot
// disagree (Jesse, 2026-10-15). Editing a word below fails test/privacyVersion.test.js until the
// version, the label and the pinned hash are moved together.
function usePrivacyLastUpdated() {
  const [label, setLabel] = useState(null);
  useEffect(() => {
    let live = true;
    axios.get(`${API_URL}/api/account-consent/documents`)
      .then((r) => {
        const d = (r.data?.documents || []).find((x) => x.key === 'privacy');
        if (live && d?.lastUpdated) setLabel(d.lastUpdated);
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  return label;
}

export default function Privacy() {
  const lastUpdated = usePrivacyLastUpdated();
  return (
    <div className="min-h-screen bg-white">
      {/* 🔑 The landing page's header and menu (Jesse, 2026-10-19): it sits in the flow and pads for the safe area,
          so the top of the page is never under it, on the web or in the app. Outside <main>, so outside the policy hash. */}
      <LandingNav />
      <main className="max-w-3xl mx-auto px-6 pt-10 pb-12">
        <h1 className="text-3xl font-bold text-black mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">
          Go Doorly, LLC · NMLS #2764749 · Effective May 1, 2025{lastUpdated ? ` · Last Updated ${lastUpdated}` : ''}
        </p>

        <section className="space-y-6 text-sm text-gray-700 leading-relaxed">
          <div>
            <h2 className="text-lg font-bold text-black mb-2">1. Introduction</h2>
            <p>
              Go Doorly, LLC ("Doorly," "we," "our," or "us"), NMLS #2764749, is committed
              to protecting your personal and financial information. This Privacy Policy
              describes how we collect, use, share, and protect information in connection
              with our mortgage lending services, online platform, and related products
              offered at{' '}
              <a href="https://app.godoorly.com" className="text-blue-600 underline">
                app.godoorly.com
              </a>.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">2. About This Notice — GLBA Financial Privacy Notice</h2>
            <p>
              Doorly is a financial institution subject to the Gramm-Leach-Bliley Act
              (GLBA), 15 U.S.C. § 6801 et seq., and the Federal Trade Commission's
              Privacy of Consumer Financial Information Rule (16 C.F.R. Part 313). This
              notice explains our privacy practices with respect to "nonpublic personal
              information" (NPI) as defined under GLBA.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">3. Information We Collect</h2>
            <p>We may collect the following categories of information:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>
                <strong>Identity Information:</strong> Full name, date of birth,
                Social Security Number or ITIN, government-issued ID
              </li>
              <li>
                <strong>Financial Information:</strong> Income, employment history,
                assets, liabilities, bank account information, credit history, credit
                scores, tax returns, pay stubs, and bank statements
              </li>
              <li>
                <strong>Property Information:</strong> Subject property address,
                purchase price, appraisal data, title information, and insurance
                details
              </li>
              <li>
                <strong>Transaction Information:</strong> Loan amount, loan purpose,
                payment history, and details of your mortgage transaction
              </li>
              <li>
                <strong>Contact Information:</strong> Mailing address, email address,
                and phone number
              </li>
              <li>
                <strong>Technical Information:</strong> IP address, browser type,
                device identifiers, and usage data collected through cookies and
                similar technologies when you use our platform
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">4. How We Collect Information</h2>
            <p>We collect information:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>
                <strong>Directly from you</strong> when you apply for a loan, create
                an account, or communicate with us
              </li>
              <li>
                <strong>From third parties</strong> including credit bureaus,
                employers, financial institutions, appraisers, title companies, and
                government agencies in connection with your loan application
              </li>
              <li>
                <strong>Automatically</strong> through our platform via cookies and
                similar tracking technologies
              </li>
              {/* 🚨 THE FOURTH SOURCE, AND THE ONLY ONE WHERE THE PERSON GAVE US
                  NOTHING. The three bullets above all describe an applicant: they
                  applied, or a third party was asked about their application, or
                  they used the platform. A real-estate or mortgage professional
                  whose details a partner provides has done none of that, and the
                  policy did not describe holding their data at all.

                  ⚠️ IT COVERS STAGED AND COMMITTED ALIKE. An uploaded list is held
                  from the moment it is uploaded, not from the moment it is
                  confirmed, and a bullet that only described what survives
                  confirmation would describe the smaller half. */}
              <li>
                <strong>From our business partners</strong> — a real-estate or mortgage
                professional who works with us may provide the business contact details
                (name, email address, telephone number) of other professionals they work
                with, so that we can contact them about becoming a Doorly partner. We
                hold these details from the point they are provided to us, including
                while an upload is still being reviewed and before it is confirmed. We
                ask the person providing them to confirm that they have an existing
                business relationship with those individuals and that they are permitted
                to share the details with us. If you are one of those individuals and
                would like your details removed, contact us using the details below and
                we will delete them.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">5. How We Use Your Information</h2>
            <p>We use your nonpublic personal information to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Evaluate and process your mortgage loan application</li>
              <li>Determine your eligibility and creditworthiness</li>
              <li>Service your loan and manage your account</li>
              <li>
                Comply with federal and state legal and regulatory requirements,
                including Ability-to-Repay (ATR) rules under Regulation Z
              </li>
              <li>Detect and prevent fraud and unauthorized activity</li>
              <li>
                Communicate with you about your loan, application status, and account
              </li>
              <li>Improve our platform and services</li>
            </ul>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">6. How We Share Your Information — GLBA Disclosure</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-3">
              <p className="font-bold text-black mb-2">GLBA Required Notice</p>
              <p>
                We may share your nonpublic personal information with the following
                categories of third parties:
              </p>
              <p className="font-bold text-black mt-3 mb-1">
                Permitted Sharing (no opt-out required):
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  Service providers and vendors who perform services on our behalf
                  (e.g., appraisers, title companies, credit reporting agencies,
                  document preparation services, identity verification providers, and
                  technology service providers), subject to confidentiality agreements
                </li>
                <li>
                  Government agencies, regulators, and law enforcement as required by
                  law
                </li>
                <li>
                  Parties necessary to complete your mortgage transaction (e.g.,
                  settlement agents, investors, warehouse lenders)
                </li>
                <li>
                  Successors in connection with a merger, acquisition, or sale of
                  assets
                </li>
              </ul>
              {/* Jesse's text, verbatim (2026-10-19). */}
              <p className="mt-3">
                <strong className="text-black">Biometric information.</strong> To verify your identity, we use Persona
                Identities, Inc. (&quot;Persona&quot;), our identity verification provider, to capture a scan of your face
                geometry from a selfie and compare it with the photo on your government-issued ID. We collect this
                information only with your consent and use it only to verify your identity and help prevent fraud. Go
                Doorly, LLC does not sell, lease, or trade your biometric information. Persona processes this information
                on our behalf and under its own privacy policy, and retains it for no more than three years. You may
                choose to verify your identity by uploading a photo of your ID instead. If you upload your ID, we use
                automated tools, including an AI service run by a third-party provider on our behalf, to read the
                document and compare it with your application. Our team reviews any ID the automated check can&apos;t
                confirm. We don&apos;t collect face geometry from IDs you upload.
              </p>
              <p className="font-bold text-black mt-3 mb-1">
                Joint Marketing (opt-out available):
              </p>
              <p>
                We do not share your information with non-affiliated third parties
                for joint marketing purposes without providing you an opportunity to
                opt out.
              </p>
              <p className="font-bold text-black mt-3 mb-1">
                Internal collaboration tools:
              </p>
              <p>
                Doorly staff receive automated notifications about your loan in
                <strong> Slack</strong>, a workplace messaging service operated by
                Slack Technologies, LLC (a Salesforce company). The following is
                transmitted to Slack when your file reaches a closing milestone:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your name and the property address</li>
                <li>Your Doorly loan number</li>
                <li>
                  For wire notifications, the amount received and the wire
                  reference number
                </li>
              </ul>
              <p className="mt-2">
                No Social Security number, date of birth, credit report, or income
                or asset documentation is sent to Slack, and no documents or
                document links are attached to these notifications. Messages
                remain in Doorly&rsquo;s Slack workspace under that
                workspace&rsquo;s retention settings.
              </p>
              <p className="font-bold text-black mt-3">
                We do not sell your personal information.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">7. Your Opt-Out Rights</h2>
            <p>
              To the extent we share your information with non-affiliated third
              parties for purposes other than those described above, you have the
              right to opt out. To opt out, contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong>{' '}
              <a href="mailto:privacy@godoorly.com" className="text-blue-600 underline">
                privacy@godoorly.com
              </a>
              <br />
              <strong>Mail:</strong> Go Doorly, LLC, 8708 S Congress Ave, Ste E500-202,
              Austin, TX 78745
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">8. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar technologies to operate our platform,
              remember your preferences, and analyze usage. You may disable cookies
              through your browser settings, though some features of our platform may
              not function properly if you do so. We do not respond to "Do Not Track"
              signals at this time.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">9. Data Security</h2>
            <p>
              We maintain physical, electronic, and procedural safeguards that comply
              with federal standards to protect your nonpublic personal information.
              Access to your information is restricted to employees and service
              providers who need it to provide services to you. All data transmitted
              through our platform is encrypted using industry-standard TLS/SSL
              protocols.
            </p>
          </div>

          {/* ⚠️ THE SECTION THAT DID NOT EXIST. Borrower documents — pay stubs,
              W-2s, bank statements, tax forms, rent/mortgage verifications,
              investment and retirement statements, gift letters — are sent to
              Anthropic by utils/documentOCR.js for extraction, including scanned
              images. Chat transcripts go the same way. None of it was disclosed.
              This is the most sensitive third-party flow in the system and it
              was the only one with no language at all. */}
          <div>
            <h2 className="text-lg font-bold text-black mb-2">9a. Artificial Intelligence and Automated Assistance</h2>
            <p className="font-semibold text-black mb-2">
              We use AI to help operate our platform, and some of your information is processed by
              an AI provider.
            </p>
            <p>
              Doorly uses AI services provided by <strong>Anthropic PBC</strong> ("Anthropic"). The
              following information is transmitted to Anthropic:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>
                <strong>Messages you send to our assistant.</strong> Our website and app include an
                automated assistant ("Eva") that answers questions. Everything you type is sent to
                Anthropic to generate a response. This applies whether or not you have an account —
                the assistant on our help centre is available without signing in.
              </li>
              <li>
                <strong>Documents you upload.</strong> When you submit documents such as pay stubs,
                W-2s, bank statements, tax forms, rent or mortgage verifications, investment or
                retirement statements, or gift letters, the contents of those documents — including
                scanned images — are sent to Anthropic to extract the information we need. This
                means financial documents you upload are processed by a third party.
              </li>
              <li>
                <strong>Communications you send us.</strong> Email you send to our support addresses
                may be processed by AI to classify it and route it to the right team.
              </li>
            </ul>
            <p className="mt-3">
              <strong>What AI does not do.</strong> AI does not decide whether your application is
              approved or declined. It does not set your rate, your terms, or your eligibility.
              Those decisions are made by our underwriting team and by rules we control. Our
              assistant is limited to general information and is not permitted to give advice about
              your specific circumstances.
            </p>
            <p className="mt-3">
              <strong>Records we keep.</strong> We retain the messages you exchange with our
              assistant, and the information extracted from your documents, as part of your file.
              See Data Retention below.
            </p>
            <p className="mt-3">
              <strong>Your choices.</strong> You are not required to use the assistant. You can
              contact us by email or phone instead, and doing so will not affect your application.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">10. Data Retention</h2>
            <p className="font-semibold text-black mb-2">
              We keep different information for different lengths of time, and some of it we are
              required by law to keep.
            </p>
            <p>
              <strong>Application records — 25 months minimum.</strong> Federal law (Regulation B,
              12 C.F.R. § 1002.12) requires us to retain records of your credit application, and of
              any decision we made on it, for at least 25 months after we notify you of that
              decision. This includes your application, the information you supplied, and any
              adverse action notice we sent. <strong>We cannot delete these records on request
              during that period</strong>, even if you close your account.
            </p>
            <p className="mt-3">
              <strong>Documents you upload.</strong> Documents such as tax returns, pay stubs, bank
              statements and identification are retained as part of your application file, subject
              to the requirement above and to other applicable recordkeeping rules.
            </p>
            <p className="mt-3">
              <strong>Messages and assistant transcripts.</strong> Messages you exchange with our
              team, and conversations with our automated assistant, are retained as part of your
              file. See section 9a above.
            </p>
            <p className="mt-3">
              <strong>Declined or closed applications.</strong> We retain your file after a
              decision. A declined application is retained for the same 25-month period, because
              the record of the decision is itself what the law requires us to keep.
            </p>
            <p className="mt-3">
              <strong>Your rights, and their limits.</strong> If you live in Texas or Florida you
              have the right to request deletion of personal information we hold about you. We will
              honour that request <strong>except</strong> where we are legally required to retain
              the information — which, for a credit application, means most of your file for 25
              months. Where we cannot delete, we will tell you why. You can{' '}
              <a href="/delete-account" className="text-blue-600 underline">request deletion here</a>.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">11. Your California Privacy Rights</h2>
            <p>
              If you are a California resident, you may have additional rights under
              the California Consumer Privacy Act (CCPA) / California Privacy Rights
              Act (CPRA), including the right to know what personal information we
              collect, the right to delete personal information, and the right to
              non-discrimination for exercising your privacy rights. To submit a
              request, contact us at{' '}
              <a href="mailto:privacy@godoorly.com" className="text-blue-600 underline">
                privacy@godoorly.com
              </a>
              . Note that certain information collected and used in connection with
              mortgage lending may be exempt from CCPA under applicable financial
              information exemptions.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">12. Children's Privacy</h2>
            <p>
              Our platform is not directed to individuals under the age of 18. We do
              not knowingly collect personal information from minors. If you believe
              we have inadvertently collected information from a minor, please
              contact us immediately at{' '}
              <a href="mailto:privacy@godoorly.com" className="text-blue-600 underline">
                privacy@godoorly.com
              </a>
              .
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">13. Third-Party Links</h2>
            <p>
              Our platform may contain links to third-party websites. We are not
              responsible for the privacy practices of those sites and encourage you
              to review their privacy policies before providing any personal
              information.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">14. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we
              will post the revised policy on this page with an updated effective
              date. We encourage you to review this policy periodically. Material
              changes will be communicated to you via email or a prominent notice on
              our platform.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-bold text-black mb-2">15. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our privacy
              practices, please contact us at:
            </p>
            <p className="mt-2">
              Go Doorly, LLC<br />
              NMLS #2764749<br />
              8708 S Congress Ave, Ste E500-202<br />
              Austin, TX 78745<br />
              <strong>Email:</strong>{' '}
              <a href="mailto:privacy@godoorly.com" className="text-blue-600 underline">
                privacy@godoorly.com
              </a>
              <br />
              <strong>Phone:</strong> Available through{' '}
              <a href="https://app.godoorly.com" className="text-blue-600 underline">
                app.godoorly.com
              </a>
            </p>
            <p className="mt-3 text-xs text-gray-500">
              <strong>Effective Date:</strong> May 1, 2025
              {/* The same server label as the header (it read a typed "May 6, 2026" that matched nothing). */}
              {lastUpdated ? <><br /><strong>Last Updated:</strong> {lastUpdated}</> : null}
            </p>
          </div>
        </section>

        <div className="mt-12 pt-6 border-t border-gray-200 text-xs text-gray-500 text-center">
          <p className="mb-2 font-semibold text-gray-600">
            Go Doorly, LLC | NMLS #2764749 | Equal Housing Lender
          </p>
          <Link to="/" className="text-blue-600 hover:underline">← Back to Doorly</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
